import moment from 'moment-timezone'
import { z } from 'zod'

const momentSchema = z.custom<moment.Moment>(val => moment.isMoment(val), {
  error: 'Expected a Moment instance',
})

/** Converts between ISO date strings and Moment instances, use on frontend */
export const isoDateToMoment = z.codec(z.iso.date(), momentSchema, {
  decode: isoDate => moment.utc(isoDate),
  encode: date => date.format('YYYY-MM-DD'),
})

/** Converts between ISO date strings and Date instances, use on backend */
export const isoDateToDate = z.codec(z.iso.date(), z.date(), {
  decode: isoDate => new Date(isoDate),
  encode: date => date.toISOString().slice(0, 10),
})

/**
 * Wraps a string schema to be nullable, treating empty strings as null.
 *
 * @example
 * ```ts
 * const schema = nullableString(z.string().trim().max(500))
 * schema.parse('')    // → null
 * schema.parse(null)  // → null
 * schema.parse('hi')  // → 'hi'
 * ```
 */
export const nullableString = (schema: z.ZodString) =>
  z.preprocess(val => (val === '' ? null : val), schema.nullable())

/**
 * Can be used to validate that an array of objects has unique values for a given key.
 *
 * @example
 * ```ts
 * const schema = z.array(z.object({ id: z.string() })).refine(uniqueBy('id'))
 * ```
 */
export const uniqueBy =
  <T>(key: keyof T, message?: string) =>
  (items: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<unknown>()
    items.forEach((item, index) => {
      const value = item[key]
      if (seen.has(value)) {
        ctx.addIssue({
          code: 'custom',
          message: message ?? `Duplicate ${String(key)}: ${value}`,
          path: [index, key as string],
        })
      }
      seen.add(value)
    })
  }
